import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart3, FileText, IndianRupee, Download } from "lucide-react";
import api, { inr, errMsg } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import RecordsReport from "../components/RecordsReport";
import PendingOrdersReport from "../components/PendingOrdersReport";
import PendingSummaryReport from "../components/PendingSummaryReport";

const TYPES = [
  { key: "date", label: "Date-wise" }, { key: "customer", label: "Customer-wise" },
  { key: "location", label: "Location-wise" }, { key: "destination", label: "Destination-wise" },
  { key: "product", label: "Product-wise" }, { key: "most_selling", label: "Most Selling" },
  { key: "price", label: "Price-wise" }, { key: "employee", label: "Employee-wise" },
  { key: "month", label: "Monthly" }, { key: "total", label: "Total" },
  { key: "pending_orders", label: "Pending Orders" }, { key: "pending_summary", label: "Pending Summary" },
];
const QTY_TYPES = ["product", "most_selling", "price"];

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canExport = isAdmin || (user.permissions || []).includes("export_data");
  const [sp] = useSearchParams();
  const initialType = TYPES.some((t) => t.key === sp.get("type")) ? sp.get("type") : "date";
  const [reportType, setReportType] = useState(initialType);
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", customer: "", employee: "", status: "" });
  const [data, setData] = useState({ rows: [], totalCount: 0, totalNet: 0 });

  const load = useCallback(async () => {
    const params = { reportType, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
    const { data } = await api.get("/reports", { params });
    setData(data);
  }, [reportType, filters]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const showQty = QTY_TYPES.includes(reportType);
  const isPending = reportType.startsWith("pending");

  const exportReport = async (fmt) => {
    try {
      const params = { reportType, format: fmt, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) };
      const { data, headers } = await api.get("/reports/export", { params, responseType: "blob" });
      const cd = headers["content-disposition"] || ""; const m = cd.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = m ? m[1] : `report.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success(`Exported (${fmt})`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="OA analytics & summaries"
        actions={canExport && !isPending && <div className="flex gap-2">
          <button className="btn-outline btn-sm" onClick={() => exportReport("excel")} data-testid="report-export-excel"><Download className="h-4 w-4" /> Excel</button>
          <button className="btn-outline btn-sm" onClick={() => exportReport("csv")} data-testid="report-export-csv"><Download className="h-4 w-4" /> CSV</button>
          <button className="btn-outline btn-sm" onClick={() => exportReport("pdf")} data-testid="report-export-pdf"><Download className="h-4 w-4" /> PDF</button>
        </div>} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button key={t.key} onClick={() => setReportType(t.key)} data-testid={`report-type-${t.key}`}
              className={`btn-sm rounded-lg px-4 font-semibold ${reportType === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {reportType === "pending_orders" && <PendingOrdersReport />}
        {reportType === "pending_summary" && <PendingSummaryReport />}

        {!isPending && (<>
        <div className="card p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <input type="date" className="input" value={filters.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} data-testid="report-date-from" />
          <input type="date" className="input" value={filters.dateTo} onChange={(e) => set("dateTo", e.target.value)} data-testid="report-date-to" />
          <input className="input" placeholder="Customer" value={filters.customer} onChange={(e) => set("customer", e.target.value)} />
          {isAdmin && <input className="input" placeholder="Employee" value={filters.employee} onChange={(e) => set("employee", e.target.value)} />}
          <select className="input" value={filters.status} onChange={(e) => set("status", e.target.value)}>
            <option value="">All Status</option><option value="draft">Draft</option><option value="generated">Generated</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="card p-5 flex items-center gap-4"><div className="h-12 w-12 rounded-xl bg-slate-900 text-white flex items-center justify-center"><FileText className="h-6 w-6" /></div><div><div className="text-2xl font-head font-bold" data-testid="report-total-count">{data.totalCount}</div><div className="text-xs text-slate-500 uppercase">Total OA</div></div></div>
          <div className="card p-5 flex items-center gap-4"><div className="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><IndianRupee className="h-6 w-6" /></div><div><div className="text-2xl font-head font-bold" data-testid="report-total-net">{inr(data.totalNet)}</div><div className="text-xs text-slate-500 uppercase">Total Net Amount</div></div></div>
        </div>

        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 font-head font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-amber-600" /> {TYPES.find((t) => t.key === reportType)?.label} Report</div>
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="th">{showQty ? "Product" : "Group"}</th><th className="th">Count</th>{showQty && <th className="th">Qty</th>}{reportType === "price" && <th className="th">Unit Rate</th>}<th className="th">Net Total</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.length === 0 && <tr><td className="td text-slate-400" colSpan={5}>No data.</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50" data-testid={`report-row-${i}`}>
                  <td className="td font-semibold text-slate-900">{r.key || "—"}</td>
                  <td className="td">{r.count}</td>
                  {showQty && <td className="td">{r.qty}</td>}
                  {reportType === "price" && <td className="td">{inr(r.unitRate)}</td>}
                  <td className="td font-semibold">{inr(r.netTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>)}

        <RecordsReport />
      </div>
    </div>
  );
}
